
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useClientsSupabaseStore } from '@/store/api/clientsSupabaseStore';
import { getBookingsForClass } from '@/services/bookingsService';
import { AttendeeItem } from '../types';
import { checkBookingEligibility, type EligibilityReason } from '@/services/edgeFunctionWrappers/bookingsRpcWrapper';
import { trialProspectsApi } from '@/services/edgeFunctionWrappers/trialProspectsWrapper';
import { useUserStore } from '@/store/userStore';
import { toast } from 'sonner';
import i18n from '@/i18n';

type TrialAttendeeProspect = Awaited<ReturnType<typeof trialProspectsApi.listByClasses>>[number];

/**
 * Hook to manage attendees for class bookings.
 *
 * IMPORTANT: La sélection (`selectedIds`) est conservée indépendamment de la
 * recherche (`searchQuery`) pour qu'un filtrage texte n'efface PAS les cases
 * déjà cochées par l'admin. Sans ça, un admin qui sélectionne A puis tape
 * pour trouver B perd la sélection de A — et un seul booking est créé.
 */
export const useBookingAttendees = (
  isAdmin: boolean,
  isCoach: boolean,
  isOpen: boolean,
  classId: string,
  dateString: string,
  totalCapacity: number
) => {
  // State
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [initialSelectedAttendees, setInitialSelectedAttendees] = useState<Set<string>>(new Set());
  const [existingBookings, setExistingBookings] = useState<{userId: string, bookingId: string, status: string}[]>([]);
  const [availableSlots, setAvailableSlots] = useState<number>(1);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [ineligibleIds, setIneligibleIds] = useState<Set<string>>(new Set());
  const [ineligibleReasons, setIneligibleReasons] = useState<Record<string, EligibilityReason>>({});
  const [bookingsLoaded, setBookingsLoaded] = useState<boolean>(false);
  const [clientsLoaded, setClientsLoaded] = useState<boolean>(false);
  const [missingProfilesLoaded, setMissingProfilesLoaded] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState<number>(0);
  const [trialAttendees, setTrialAttendees] = useState<AttendeeItem[]>([]);
  const [trialsLoaded, setTrialsLoaded] = useState<boolean>(true);
  // La vérification d'éligibilité a-t-elle échoué ? On ne peut alors ni
  // affirmer « abonnement valide » ni « abo expiré » : il faut le dire.
  const [eligibilityUnavailable, setEligibilityUnavailable] = useState<boolean>(false);

  // Get clients from store
  const { clients: storeClients, currentGymId: clientsGymId, fetchClients, fetchClientsByIds } = useClientsSupabaseStore();
  const gymId = useUserStore(s => s.gymId);
  // Ne jamais proposer les adhérents d'une autre salle : la liste en mémoire
  // peut provenir de la salle précédente après un changement de salle.
  const clientsMatchGym = !clientsGymId || clientsGymId === gymId;
  const clients = useMemo(() => (clientsMatchGym ? storeClients : []), [clientsMatchGym, storeClients]);

  // Fetch existing bookings for this class and date.
  // Returns the freshly loaded list so callers can chain on it (last-mile refresh).
  const fetchExistingBookings = useCallback(async (): Promise<{userId: string, bookingId: string, status: string}[]> => {
    if (!classId || !dateString) return [];

    try {
      const bookings = await getBookingsForClass(classId, dateString);
      console.log('[useBookingAttendees] Fetched raw bookings:', bookings.length);

      const mapped = bookings.map(b => ({
        userId: b.userId,
        bookingId: b.bookingId,
        status: b.status
      }));
      setExistingBookings(mapped);
      setBookingsLoaded(true);

      // Resolve missing profiles for any booked userId not in cache.
      const activeBookedIds = mapped.filter(b => b.status !== 'cancelled').map(b => b.userId);
      if (activeBookedIds.length > 0) {
        const cachedClientIds = new Set(clients.map(c => c.id));
        const missingUserIds = activeBookedIds.filter(id => !cachedClientIds.has(id));

        if (missingUserIds.length > 0) {
          setMissingProfilesLoaded(false);
          console.log('[useBookingAttendees] Fetching missing client profiles:', missingUserIds.length);
          try {
            await fetchClientsByIds(missingUserIds);
            console.log('[useBookingAttendees] Missing clients loaded');
          } catch (e) {
            console.error('[useBookingAttendees] Failed to load missing profiles', e);
            setLoadError('missing_profiles');
          } finally {
            setMissingProfilesLoaded(true);
          }
        } else {
          setMissingProfilesLoaded(true);
        }
      } else {
        setMissingProfilesLoaded(true);
      }
      return mapped;
    } catch (error) {
      console.error('Error fetching existing bookings:', error);
      setLoadError('bookings_fetch_failed');
      throw error;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId, dateString]);

  // Initialize data
  useEffect(() => {
    if (!isOpen) return;

    const initData = async () => {
      console.log('[useBookingAttendees] Initializing data...');
      setIsLoadingData(true);
      setLoadError(null);
      setBookingsLoaded(false);
      setTrialsLoaded(!isAdmin && !isCoach);
      setSearchQuery("");
      // Seul l'ADMIN a besoin de l'annuaire complet des adhérents (il est le
      // seul à pouvoir réserver pour un tiers). Le coach ne charge que les
      // profils des personnes déjà inscrites au cours.
      setClientsLoaded(!isAdmin || (clients.length > 0 && clientsMatchGym));
      setMissingProfilesLoaded(true);

      try {
        const promises: Promise<unknown>[] = [];
        promises.push(fetchExistingBookings().catch(() => null));

        if (isAdmin && (clients.length === 0 || !clientsMatchGym)) {
          promises.push(
            fetchClients()
              .then(() => setClientsLoaded(true))
              .catch((e) => {
                console.error('[useBookingAttendees] Error loading clients', e);
                setLoadError('clients_fetch_failed');
              })
          );
        } else {
          setClientsLoaded(true);
        }

        await Promise.all(promises);
      } catch (error) {
        console.error('[useBookingAttendees] Error loading data:', error);
        setLoadError('init_failed');
      } finally {
        setIsLoadingData(false);
      }
    };

    initData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isAdmin, isCoach, classId, dateString, reloadTick, gymId]);

  const retryLoad = useCallback(() => {
    setReloadTick(t => t + 1);
  }, []);

  // True only when every piece of data needed for a safe diff is in.
  const isFullyLoaded = bookingsLoaded && clientsLoaded && missingProfilesLoaded && trialsLoaded && !loadError;

  // Pré-calcul de l'éligibilité (abonnement valide couvrant la date du cours
  // avec capacité disponible) pour tous les clients chargés.
  // Permet d'afficher un badge "Abo expiré" et de désactiver la coche.
  useEffect(() => {
    if (!isOpen || (!isAdmin && !isCoach)) return;
    if (!gymId || !dateString) return;
    if (!clients.length) {
      setIneligibleIds(new Set());
      setEligibilityUnavailable(false);
      return;
    }

    let cancelled = false;
    const ids = clients.map(c => c.id);
    const alreadyBooked = new Set(
      existingBookings.filter(b => b.status !== 'cancelled').map(b => b.userId)
    );
    // Skip already-booked attendees (they were booked when valid; ineligibility
    // shouldn't visually un-check them).
    const toCheck = ids.filter(id => !alreadyBooked.has(id));

    checkBookingEligibility(toCheck, dateString, gymId, classId)
      .then(({ ineligible, reasons }) => {
        if (cancelled) return;
        setIneligibleIds(new Set(ineligible));
        setIneligibleReasons(reasons);
        setEligibilityUnavailable(false);
      })
      .catch(err => {
        console.error('[useBookingAttendees] eligibility check failed', err);
        if (cancelled) return;
        // Le wrapper renvoyait auparavant « tout le monde est éligible » sur
        // erreur : les badges « abo expiré » disparaissaient sans que personne
        // ne le sache. On ne bloque pas la réservation (le serveur reste seul
        // juge, cf. NO_VALID_SUBSCRIPTION_FOR_DATE) mais on prévient l'admin.
        setIneligibleIds(new Set());
        setIneligibleReasons({});
        setEligibilityUnavailable(true);
        toast.warning(
          i18n.t('attendees.eligibilityCheckFailed', { ns: 'bookingui' }),
          { id: 'eligibility-check-failed', duration: 8000 }
        );
      });

    return () => { cancelled = true; };
  }, [isOpen, isAdmin, isCoach, gymId, dateString, classId, clients, existingBookings]);

  // Initialiser `selectedIds` à partir des bookings DB actifs.
  // Ne se déclenche QUE quand existingBookings change (ouverture / refresh),
  // jamais quand l'utilisateur tape dans la recherche.
  useEffect(() => {
    const initial = new Set(
      existingBookings
        .filter(b => b.status !== 'cancelled')
        .map(b => b.userId)
    );
    setSelectedIds(initial);
    setInitialSelectedAttendees(initial);
  }, [existingBookings]);

  // Liste de base : tous les clients formatés (sans filtre texte, sans tenir
  // compte de selectedIds — on les fusionne juste après pour le rendu).
  // Fetch trial prospects (séances d'essai) — read-only attendees
  useEffect(() => {
    if (!isOpen || (!isAdmin && !isCoach)) {
      setTrialAttendees([]);
      setTrialsLoaded(true);
      return;
    }
    if (!gymId || !classId || !dateString) {
      setTrialAttendees([]);
      setTrialsLoaded(false);
      return;
    }
    let cancelled = false;
    setTrialsLoaded(false);
    trialProspectsApi.listByClasses(gymId, [classId], dateString)
      .then(trials => {
        if (cancelled) return;
        const mapped: AttendeeItem[] = (trials ?? []).map((t: TrialAttendeeProspect) => ({
          id: `trial-${t.id}`,
          name: `${t.first_name ?? ''} ${t.last_name ?? ''}`.trim(),
          selected: true,
          clothingSize: t.clothing_size ?? '',
          isSelectable: false,
          isTrial: true,
          trialPhone: t.phone ?? null,
          trialNotes: t.notes ?? null,
          trialProspectId: t.id,
          trialCoachId: t.coach_id ?? null,
          trialCoachName: t.coach_id
            ? `${t.coach_first_name ?? ''} ${t.coach_last_name ?? ''}`.trim() || null
            : null,
        }));
        setTrialAttendees(mapped);
        setTrialsLoaded(true);
      })
      .catch(err => {
        console.warn('[useBookingAttendees] trial prospects fetch failed', err);
        if (!cancelled) {
          setTrialAttendees([]);
          setLoadError('trials_fetch_failed');
          setTrialsLoaded(true);
        }
      });
    return () => { cancelled = true; };
  }, [isOpen, isAdmin, isCoach, gymId, classId, dateString, reloadTick]);

  const baseAttendees = useMemo<AttendeeItem[]>(() => {
    if (!isAdmin && !isCoach) return [];

    // Seul l'ADMIN peut sélectionner un adhérent pour réserver à sa place
    // (bookings-management refuse le coach côté serveur).
    const selectableUserIds = new Set(isAdmin ? clients.map(c => c.id) : []);

    // Le coach n'a AUCUN accès à l'annuaire des adhérents : il ne voit que
    // les personnes déjà inscrites à ce cours (lecture seule) + les essais.
    const bookedUserIds = new Set(
      existingBookings.filter(b => b.status !== 'cancelled').map(b => b.userId)
    );
    const visibleClients = isAdmin ? clients : clients.filter(c => bookedUserIds.has(c.id));

    const clientAttendees = visibleClients.map(client => {
      const isIneligible = ineligibleIds.has(client.id);
      return {
        id: client.id,
        name: `${client.firstName} ${client.lastName}`,
        selected: false,
        clothingSize: client.clothingSize,
        isSelectable: selectableUserIds.has(client.id) && !isIneligible,
        ineligible: isIneligible,
        ineligibleReason: isIneligible ? ineligibleReasons[client.id] : undefined,
      } as AttendeeItem;
    });
    return [...clientAttendees, ...trialAttendees];
  }, [clients, isAdmin, isCoach, ineligibleIds, ineligibleReasons, trialAttendees, existingBookings]);

  // `filteredAttendees` exposé : applique le filtre recherche + fusionne `selected` depuis selectedIds.
  const filteredAttendees = useMemo<AttendeeItem[]>(() => {
    const q = searchQuery.toLowerCase();
    return baseAttendees
      .filter(a => a.name.toLowerCase().includes(q))
      .map(a => ({ ...a, selected: a.isTrial ? true : selectedIds.has(a.id) }))
      .sort((a, b) => {
        if (a.selected && !b.selected) return -1;
        if (!a.selected && b.selected) return 1;
        return a.name.localeCompare(b.name);
      });
  }, [baseAttendees, searchQuery, selectedIds]);

  // Mettre à jour les places dispo en fonction des sélections + prospects (séances d'essai).
  useEffect(() => {
    setAvailableSlots(totalCapacity - selectedIds.size - trialAttendees.length);
  }, [selectedIds, totalCapacity, trialAttendees]);

  // Toggle d'un participant : on mute uniquement le Set de sélection.
  const handleAttendeeToggle = useCallback((id: string) => {
    setSelectedIds(prev => {
      // Sécurité : refuser le toggle si l'utilisateur n'est pas sélectionnable
      const target = baseAttendees.find(a => a.id === id);
      if (target && !target.isSelectable) {
        console.warn('[useBookingAttendees] Cannot toggle non-selectable user:', target.name);
        return prev;
      }

      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, [baseAttendees]);

  // Liste COMPLÈTE (non filtrée par la recherche) avec `selected` mergé.
  // À utiliser pour la confirmation, pour ne pas perdre les sélections
  // hors champ de recherche.
  const allAttendees = useMemo<AttendeeItem[]>(() => {
    // Exclude trial prospects from the diff list — they aren't real bookings.
    return baseAttendees
      .filter(a => !a.isTrial)
      .map(a => ({ ...a, selected: selectedIds.has(a.id) }));
  }, [baseAttendees, selectedIds]);

  return {
    searchQuery,
    setSearchQuery,
    filteredAttendees,
    allAttendees,
    handleAttendeeToggle,
    initialSelectedAttendees,
    existingBookings,
    availableSlots,
    setAvailableSlots,
    isLoadingClients: isLoadingData || ((isAdmin || isCoach) && !trialsLoaded),
    isFullyLoaded,
    loadError,
    retryLoad,
    fetchExistingBookings,
    eligibilityUnavailable,
  };
};
